import { useEffect, useState } from 'react';
import { preferencesManager } from '../utils/preferencesManager';
import {
  OptionsManager,
  AppOptions,
  DEFAULT_OPTIONS,
  deriveRecreationalCertifications,
  normalizeLegacyCertification,
  normalizeRecreationalCertifications,
  normalizePreviousDiveCount,
  normalizeFreediveCertifications,
  normalizeFreediveSpecialtyCertifications,
  normalizeBooleanFlagMap,
  BooleanPrivacySettingKey,
  DIVE_COUNT_RANGE_LABELS,
} from '../utils/optionsManager';
import { authService } from '../services/authService';

/**
 * Hook to sync user preferences from database to local optionsManager
 * Should be called once at app initialization (Dashboard level)
 */
export const usePreferencesSync = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const optionsManager = OptionsManager.getInstance();

  useEffect(() => {
    const loadAndSyncPreferences = async () => {
      console.log('usePreferencesSync: Hook effect running...');
      try {
        setIsLoading(true);
        setError(null);

        // Get userId from authService (which loads from localStorage)
        const user = authService.getUser();
        if (!user) {
          console.log('usePreferencesSync: No authenticated user found, using local preferences only');
          setIsLoading(false);
          return;
        }
        console.log('usePreferencesSync: Found authenticated user:', user.userId);

        // Always point preferencesManager at the authenticated user — a stale
        // demo/guest id here would read and write the wrong account's prefs
        if (preferencesManager.getUserId() !== user.userId) {
          console.log('usePreferencesSync: Setting userId in preferencesManager:', user.userId);
          preferencesManager.setUserId(user.userId);
        }

        console.log('usePreferencesSync: Loading preferences from database for user:', user.userId);
        const preferences = await preferencesManager.loadPreferences();
        console.log('usePreferencesSync: Loaded preferences:', preferences);

        // Get current options once at the beginning
        let currentOptions = optionsManager.getOptions();

        // Check if this is a new user with no preferences saved.
        // Never seed from a failed fetch: loadPreferences() returns a bare defaults
        // object when the request errors, which looks identical to a brand-new
        // account and would push local defaults over the diver's real settings.
        const hasNoPreferences =
          !preferencesManager.didLoadFail() &&
          preferences.showDepthInMeters === null &&
          preferences.showTemperatureInCelsius === null &&
          preferences.showPressureInBar === null &&
          !preferences.theme &&
          !preferences.certification &&
          !preferences.customSettings;

        if (hasNoPreferences) {
          console.log('usePreferencesSync: New user detected, saving current localStorage preferences to database');
          // Save current localStorage preferences as the user's initial preferences
          await preferencesManager.savePreferences({
            showDepthInMeters: currentOptions.units.depth === 'meters',
            showTemperatureInCelsius: currentOptions.units.temperature === 'celsius',
            showPressureInBar: currentOptions.units.pressure === 'bar',
            preferredUnit: (currentOptions.units.depth === 'meters' &&
                           currentOptions.units.temperature === 'celsius' &&
                           currentOptions.units.pressure === 'bar') ? 'metric' : 'imperial',
            theme: currentOptions.display.theme,
            certification: currentOptions.diver.certification,
            locationDisplayPreference: preferences.locationDisplayPreference || 'geotagged',
            customSettings: {
              weightUnit: currentOptions.units.weight,
              tankSize: currentOptions.equipment.tankSize,
              customTankVolume: currentOptions.equipment.customTankVolume,
              recreationalCertifications: currentOptions.diver.recreationalCertifications,
              // certificationCardPhotos intentionally not seeded — the feature is
              // disabled in Options.tsx pending a move to S3
              specialtyCertifications: currentOptions.diver.specialtyCertifications,
              gasCertifications: currentOptions.diver.gasCertifications,
              freediveCertifications: currentOptions.diver.freediveCertifications,
              freediveSpecialtyCertifications: currentOptions.diver.freediveSpecialtyCertifications,
              previousDiveCount: currentOptions.diver.previousDiveCount ?? null,
              privacy: currentOptions.privacy,
              gasConsumptionMetric: currentOptions.display.gasConsumptionMetric,
              timeFormat: currentOptions.display.timeFormat,
              dateFormat: currentOptions.display.dateFormat,
              activityTypeFilters: currentOptions.filters.activityTypes
            }
          });
          console.log('usePreferencesSync: Initial preferences saved to database');
          setIsLoading(false);
          return; // No need to sync since we just saved current state
        }

        // Build updates object for each category
        const unitsUpdates: Partial<AppOptions['units']> = {};
        const displayUpdates: Partial<AppOptions['display']> = {};
        const diverUpdates: Partial<AppOptions['diver']> = {};
        const equipmentUpdates: Partial<AppOptions['equipment']> = {};
        const filtersUpdates: Partial<AppOptions['filters']> = {};
        const privacyUpdates: Partial<AppOptions['privacy']> = {};

        // Load unit preferences (check for both null and undefined)
        if (preferences.showDepthInMeters !== undefined && preferences.showDepthInMeters !== null) {
          unitsUpdates.depth = preferences.showDepthInMeters ? 'meters' : 'feet';
        }
        if (preferences.showTemperatureInCelsius !== undefined && preferences.showTemperatureInCelsius !== null) {
          unitsUpdates.temperature = preferences.showTemperatureInCelsius ? 'celsius' : 'fahrenheit';
        }
        if (preferences.showPressureInBar !== undefined && preferences.showPressureInBar !== null) {
          unitsUpdates.pressure = preferences.showPressureInBar ? 'bar' : 'psi';
        }

        // Load theme
        if (preferences.theme) {
          displayUpdates.theme = preferences.theme as AppOptions['display']['theme'];
        }

        // Load certification (server may hold legacy multi-level instructor values)
        if (preferences.certification) {
          diverUpdates.certification = normalizeLegacyCertification(preferences.certification);
          // Accounts saved before the checkbox map existed only have the single
          // highest level — expand it cumulatively unless customSettings has the map
          if (!preferences.customSettings?.recreationalCertifications) {
            diverUpdates.recreationalCertifications = deriveRecreationalCertifications(diverUpdates.certification);
          }
        }

        // Load custom settings
        if (preferences.customSettings) {
          const custom = preferences.customSettings;

          // Weight unit (no dedicated column — carried in customSettings)
          if (custom.weightUnit === 'kg' || custom.weightUnit === 'lbs') {
            unitsUpdates.weight = custom.weightUnit;
          }

          // Tank size
          if (custom.tankSize) {
            equipmentUpdates.tankSize = custom.tankSize as AppOptions['equipment']['tankSize'];
          }

          // Custom tank volume
          if (custom.customTankVolume !== undefined) {
            equipmentUpdates.customTankVolume = custom.customTankVolume;
          }

          // Recreational/professional certifications (normalizes legacy instructor levels)
          if (custom.recreationalCertifications) {
            diverUpdates.recreationalCertifications = normalizeRecreationalCertifications(custom.recreationalCertifications);
          }

          // Certification card photos — only accept a well-formed array of entries.
          // Options.tsx maps over this and reads .length, so a malformed value
          // arriving from the server would break the settings screen outright.
          if (Array.isArray(custom.certificationCardPhotos)) {
            diverUpdates.certificationCardPhotos = custom.certificationCardPhotos.filter(
              (photo: unknown): photo is AppOptions['diver']['certificationCardPhotos'][number] =>
                !!photo &&
                typeof photo === 'object' &&
                typeof (photo as { id?: unknown }).id === 'string' &&
                typeof (photo as { dataUrl?: unknown }).dataUrl === 'string'
            );
          }

          // Specialty and gas certifications — normalized against the defaults for
          // the same reason as the freedive maps: drop unknown keys and accept only
          // real `true` flags so a malformed payload can't inject junk or a
          // truthy-but-not-boolean value into the profile.
          if (custom.specialtyCertifications) {
            diverUpdates.specialtyCertifications = normalizeBooleanFlagMap(
              custom.specialtyCertifications,
              DEFAULT_OPTIONS.diver.specialtyCertifications
            );
          }

          if (custom.gasCertifications) {
            diverUpdates.gasCertifications = normalizeBooleanFlagMap(
              custom.gasCertifications,
              DEFAULT_OPTIONS.diver.gasCertifications
            );
          }

          // Freediving (apnea) certifications and specialties
          if (custom.freediveCertifications) {
            diverUpdates.freediveCertifications =
              normalizeFreediveCertifications(custom.freediveCertifications);
          }

          if (custom.freediveSpecialtyCertifications) {
            diverUpdates.freediveSpecialtyCertifications =
              normalizeFreediveSpecialtyCertifications(custom.freediveSpecialtyCertifications);
          }

          // Manually entered dive count (saved as null when the diver clears it)
          if (custom.previousDiveCount !== undefined && custom.previousDiveCount !== null) {
            diverUpdates.previousDiveCount = normalizePreviousDiveCount(custom.previousDiveCount);
          }

          // Time format
          if (custom.timeFormat) {
            displayUpdates.timeFormat = custom.timeFormat as AppOptions['display']['timeFormat'];
          }

          // Gas consumption metric
          if (custom.gasConsumptionMetric) {
            displayUpdates.gasConsumptionMetric = custom.gasConsumptionMetric as AppOptions['display']['gasConsumptionMetric'];
          }

          // Date format
          if (custom.dateFormat) {
            displayUpdates.dateFormat = custom.dateFormat as AppOptions['display']['dateFormat'];
          }

          // Activity type filters. These default to `true`, so normalizeBooleanFlagMap
          // (which only honors an explicit `true`) would silently turn every unlisted
          // type off — copy real booleans for known keys instead.
          if (custom.activityTypeFilters && typeof custom.activityTypeFilters === 'object') {
            const activityTypes = { ...DEFAULT_OPTIONS.filters.activityTypes };
            for (const key of Object.keys(activityTypes) as Array<keyof typeof activityTypes>) {
              const value = (custom.activityTypeFilters as Record<string, unknown>)[key];
              if (typeof value === 'boolean') {
                activityTypes[key] = value;
              }
            }
            filtersUpdates.activityTypes = activityTypes;
          }

          // Privacy settings — only copy real booleans for known keys (or, for
          // diveCountDisplay/diveCountRangeOverride, one of their known string
          // values) so a malformed stored value can't turn into an unintended opt-in
          if (custom.privacy && typeof custom.privacy === 'object') {
            const rawPrivacy = custom.privacy as Record<string, unknown>;
            for (const key of Object.keys(DEFAULT_OPTIONS.privacy) as Array<keyof AppOptions['privacy']>) {
              const value = rawPrivacy[key];
              if (key === 'diveCountDisplay') {
                if (value === 'exact' || value === 'range') {
                  privacyUpdates.diveCountDisplay = value;
                }
              } else if (key === 'diveCountRangeOverride') {
                privacyUpdates.diveCountRangeOverride =
                  typeof value === 'string' && (DIVE_COUNT_RANGE_LABELS as readonly string[]).includes(value)
                    ? value
                    : null;
              } else if (typeof value === 'boolean') {
                privacyUpdates[key as BooleanPrivacySettingKey] = value;
              }
            }
          }
        }

        // Check if any updates need to be applied
        const hasUpdates =
          Object.keys(unitsUpdates).length > 0 ||
          Object.keys(displayUpdates).length > 0 ||
          Object.keys(diverUpdates).length > 0 ||
          Object.keys(equipmentUpdates).length > 0 ||
          Object.keys(filtersUpdates).length > 0 ||
          Object.keys(privacyUpdates).length > 0;

        if (!hasUpdates) {
          console.log('usePreferencesSync: No preference updates needed, database matches localStorage');
          setIsLoading(false);
          return;
        }

        // Build complete updated options object
        const updatedOptions = {
          units: { ...currentOptions.units, ...unitsUpdates },
          display: { ...currentOptions.display, ...displayUpdates },
          diver: { ...currentOptions.diver, ...diverUpdates },
          equipment: { ...currentOptions.equipment, ...equipmentUpdates },
          filters: { ...currentOptions.filters, ...filtersUpdates },
          privacy: { ...currentOptions.privacy, ...privacyUpdates }
        };

        // CRITICAL: First save to localStorage directly
        // This ensures localStorage is updated BEFORE any components try to read from it
        const OPTIONS_STORAGE_KEY = 'aidivecoach_options';
        localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(updatedOptions));
        console.log('usePreferencesSync: Wrote updated preferences directly to localStorage');

        // Then reload optionsManager from localStorage and notify all subscribers
        // This ensures all components get the fresh data
        console.log('usePreferencesSync: Applying preference updates from database:', {
          unitsUpdates,
          displayUpdates,
          diverUpdates,
          equipmentUpdates,
          filtersUpdates
        });

        optionsManager.reloadFromStorage();

        console.log('usePreferencesSync: Successfully synced all preferences from database to localStorage');
        console.log('usePreferencesSync: Final options after sync:', optionsManager.getOptions());
        setIsLoading(false);
      } catch (err) {
        console.error('usePreferencesSync: Failed to load preferences from database:', err);
        setError(err as Error);
        setIsLoading(false);
        // Don't throw - we can still use local preferences
      }
    };

    let lastSyncedUserId: string | null = null;

    const syncIfUserChanged = () => {
      const user = authService.getUser();
      if (!user) {
        lastSyncedUserId = null;
        setIsLoading(false);
        return;
      }
      if (user.userId === lastSyncedUserId) {
        return;
      }
      lastSyncedUserId = user.userId;
      loadAndSyncPreferences();
    };

    syncIfUserChanged();
    // Re-sync when auth state changes, so preferences load immediately after
    // logging in instead of waiting for a full page refresh
    const unsubscribe = authService.subscribe(syncIfUserChanged);
    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // optionsManager is a singleton and doesn't change

  return { isLoading, error };
};
